/**
 * D-Rating: índice de rendimiento del jugador.
 *
 * El cálculo real vive en el servidor (`server/game.ts`), que es la única
 * autoridad sobre los resultados. Aquí solo queda lo necesario para
 * presentarlo: umbrales, nombres de rango y colores.
 */

export const INITIAL_RATING = 1000;

export interface Tier {
  id: string;
  label: string;
  icon: string;
  min: number;
  /** Clases Tailwind para insignias y tarjetas. */
  chip: string;
  text: string;
}

/**
 * Escalera de rangos: la cacería del dahu.
 *
 * El dahu es una criatura del folclore alpino que nadie ha visto nunca. Cada
 * rango es un paso en su búsqueda, de oír hablar de él a volverse leyenda:
 * empiezas siendo un rumor y terminas siendo un mito.
 */
export const TIERS: Tier[] = [
  {
    id: 'rumor',
    label: 'Rumor',
    icon: '🍃',
    min: 0,
    chip: 'bg-ink-700/40 border-ink-500',
    text: 'text-ink-300',
  },
  {
    id: 'rastro',
    label: 'Rastro',
    icon: '🐾',
    min: 1000,
    chip: 'bg-success-500/15 border-success-500',
    text: 'text-success-400',
  },
  {
    id: 'avistamiento',
    label: 'Avistamiento',
    icon: '🔭',
    min: 1200,
    chip: 'bg-sky-500/15 border-sky-500',
    text: 'text-sky-400',
  },
  {
    id: 'leyenda',
    label: 'Leyenda',
    icon: '📜',
    min: 1400,
    chip: 'bg-violet-500/15 border-violet-500',
    text: 'text-violet-400',
  },
  {
    id: 'mito',
    label: 'Mito',
    icon: '✨',
    min: 1600,
    chip: 'bg-ember-500/15 border-ember-500',
    text: 'text-ember-400',
  },
];

export function tierForRating(rating: number): Tier {
  // Del más alto al más bajo: el primero que alcanza es el suyo.
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (rating >= TIERS[i].min) return TIERS[i];
  }
  return TIERS[0];
}

/** Rango siguiente, o `null` si ya está en el más alto. */
export function nextTier(rating: number): Tier | null {
  return TIERS.find((t) => t.min > rating) ?? null;
}

/** Progreso hacia el siguiente rango, de 0 a 1. */
export function tierProgress(rating: number): number {
  const current = tierForRating(rating);
  const next = nextTier(rating);
  if (!next) return 1;
  return Math.min(1, Math.max(0, (rating - current.min) / (next.min - current.min)));
}
